import React from "react";
import { FiGlobe, FiMonitor, FiSmartphone } from "react-icons/fi";

// ── Design tokens (mirrors WalletDashboard) ──────────────────────────────────
export const C = {
  bg:         "#f4f6fb",
  panel:      "#ffffff",
  header:     "#1a2b4a",
  blue:       "#2c7be5",
  blueSoft:   "#eaf2ff",
  teal:       "#00b69b",
  tealSoft:   "#e6faf7",
  orange:     "#f6961e",
  orangeSoft: "#fff4e6",
  red:        "#e63757",
  redSoft:    "#fff0f3",
  purple:     "#7048e8",
  purpleSoft: "#f1edff",
  text:       "#1a2b4a",
  textMid:    "#4a5568",
  textLight:  "#8898aa",
  border:     "#e2e8f0",
  inputBg:    "#f8faff",
  shadow:     "0 2px 12px rgba(26,43,74,0.08)",
  radius:     "10px",
  radiusSm:   "6px",
};

// Device targeting — what each value ACTUALLY controls, per surface:
//   • Android & iOS apps request the slot with deviceType=mobile, so they
//     receive banners tagged "mobile" or "all" (never "desktop").
//   • The web landing page fetches ALL banners for the slot and then picks
//     desktop vs mobile client-side from the viewport width.
// Since Aug 29 2026 the value is DERIVED from which artwork a hero banner
// carries (see deriveDeviceType) — the form no longer asks for it.
export const DEVICE_META = {
  all: {
    label: "All",
    color: C.blue, bg: C.blueSoft, icon: <FiGlobe size={11} />,
    detail: "Shows everywhere: Android & iOS apps, desktop web, and mobile web.",
  },
  desktop: {
    label: "Desktop web",
    color: C.purple, bg: C.purpleSoft, icon: <FiMonitor size={11} />,
    detail: "Desktop-width web browsers only (lusuku.ug). Never shown in the mobile apps or on mobile web.",
  },
  mobile: {
    label: "Mobile",
    color: C.teal, bg: C.tealSoft, icon: <FiSmartphone size={11} />,
    detail: "Android & iOS apps plus mobile-width web browsers. Not shown on desktop web.",
  },
};

export const LINK_META = {
  none:            { label: "None",     color: C.textLight, bg: "#f0f2f5" },
  category:        { label: "Category", color: C.orange,    bg: C.orangeSoft },
  vendor:          { label: "Shop",     color: C.teal,      bg: C.tealSoft },
  product:         { label: "Product",  color: C.purple,    bg: C.purpleSoft },
  deal:            { label: "Deal",     color: C.red,       bg: C.redSoft },
  internal_screen: { label: "Screen",   color: C.blue,      bg: C.blueSoft },
  external:        { label: "Link",     color: C.blue,      bg: C.blueSoft },
};

// Plain-language names for the verticals a product or shop link can point at.
export const LINK_KIND_LABEL = {
  brand: "brand shop",
  restaurant: "restaurant",
  retail: "retail shop",
  healthcare: "pharmacy",
};

// ── Artwork canon, per slot AND per surface ───────────────────────────────────
// MUST mirror `SLOT_CANONICAL_DIMENSIONS` / `SLOT_DESKTOP_CANONICAL_DIMENSIONS`
// in controllers/advertismentController.js and the `slot` enum in
// models/advertismentModel.js.
//
// Each artwork entry drives one upload box in the form:
//   - key           : "phone" | "desktop" — which surface the artwork serves
//   - field         : the Advertisement field the URL is saved to
//   - aspectRatio   : enforced by the ReactCrop overlay
//   - canonWidthPx  : output canvas width  (matches Cloudinary transform w_)
//   - canonHeightPx : output canvas height (matches Cloudinary transform h_)
//   - minWidthPx    : source files narrower than this are refused (would be
//                     enlarged more than 2x and look soft on every screen)
//
// The hero has TWO artworks because the desktop landing page draws the slot
// as a fixed 3159×498 strip (Lusuku_Web LandingPage.jsx `heroFrame`) while
// the apps and mobile web draw a 16:9 card. One crop cannot serve both.
export const PHONE_ARTWORK_KEY = "phone";
export const DESKTOP_ARTWORK_KEY = "desktop";

const HERO_PHONE_ARTWORK = {
  key: PHONE_ARTWORK_KEY,
  field: "imageUrl",
  label: "Phone artwork",
  surfaces: "Android & iOS apps and mobile web",
  aspectRatio: 16 / 9,
  canonWidthPx: 1600, canonHeightPx: 900, displayLabel: "1600×900 (16:9)",
  minWidthPx: 800,
};

const HERO_DESKTOP_ARTWORK = {
  key: DESKTOP_ARTWORK_KEY,
  field: "desktopImageUrl",
  label: "Desktop web artwork",
  surfaces: "the desktop landing page (lusuku.ug)",
  aspectRatio: 3159 / 498,
  canonWidthPx: 3159, canonHeightPx: 498, displayLabel: "3159×498 (6.34:1)",
  minWidthPx: 1580,
};

const singleArtwork = (canonWidthPx, canonHeightPx, aspectRatio, displayLabel, surfaces) => ({
  key: PHONE_ARTWORK_KEY,
  field: "imageUrl",
  label: "Artwork",
  surfaces,
  aspectRatio, canonWidthPx, canonHeightPx, displayLabel,
  minWidthPx: Math.round(canonWidthPx / 2),
});

// `renders` states exactly where each slot appears, verified against the
// client code that fetches it. The reserved slots are rendered by NO client
// yet — saying so here prevents an admin from uploading artwork that
// silently never appears anywhere.
export const SLOT_META = {
  hero_carousel: {
    label: "Hero Carousel (Home)", aspectRatio: 16 / 9,
    canonWidthPx: 1600, canonHeightPx: 900, displayLabel: "1600×900 phone, 3159×498 desktop",
    renders: "The main carousel at the top of the customer HOME screen in the Android & iOS apps, the top of mobile web, AND the wide hero strip on the desktop landing page (lusuku.ug). The most visible placement on the platform. Phones and desktop are different shapes, so each has its own artwork below.",
    reserved: false,
    artworks: [HERO_PHONE_ARTWORK, HERO_DESKTOP_ARTWORK],
  },
  retail_carousel: {
    label: "Retail Carousel (Shop screen)", aspectRatio: 2,
    canonWidthPx: 1600, canonHeightPx: 800, displayLabel: "1600×800 (2:1)",
    renders: "The banner strip at the top of the retail SHOP screen (Shop Groceries) in the Android & iOS apps. Not shown on web.",
    reserved: false,
    artworks: [singleArtwork(1600, 800, 2, "1600×800 (2:1)", "the Android & iOS apps")],
  },
  deals_banner: {
    label: "Deals Banner", aspectRatio: 21 / 9,
    canonWidthPx: 2100, canonHeightPx: 900, displayLabel: "2100×900 (21:9)",
    renders: "RESERVED — no app or web screen renders this slot yet. Banners saved here will not appear anywhere until the Deals screen placement ships.",
    reserved: true,
    artworks: [singleArtwork(2100, 900, 21 / 9, "2100×900 (21:9)", "the Deals screen (not built yet)")],
  },
  secondary_promo: {
    label: "Secondary Promo", aspectRatio: 1,
    canonWidthPx: 1080, canonHeightPx: 1080, displayLabel: "1080×1080 (1:1)",
    renders: "RESERVED — no app or web screen renders this slot yet.",
    reserved: true,
    artworks: [singleArtwork(1080, 1080, 1, "1080×1080 (1:1)", "nothing yet")],
  },
  category_card: {
    label: "Category Card", aspectRatio: 4 / 3,
    canonWidthPx: 1200, canonHeightPx: 900, displayLabel: "1200×900 (4:3)",
    renders: "RESERVED — no app or web screen renders this slot yet.",
    reserved: true,
    artworks: [singleArtwork(1200, 900, 4 / 3, "1200×900 (4:3)", "nothing yet")],
  },
  checkout_promo: {
    label: "Checkout Promo", aspectRatio: 16 / 9,
    canonWidthPx: 1600, canonHeightPx: 900, displayLabel: "1600×900 (16:9)",
    renders: "RESERVED — no app or web screen renders this slot yet.",
    reserved: true,
    artworks: [singleArtwork(1600, 900, 16 / 9, "1600×900 (16:9)", "nothing yet")],
  },
};

export const slotHasDesktopArtwork = (slot) =>
  !!SLOT_META[slot]?.artworks?.some((a) => a.key === DESKTOP_ARTWORK_KEY);

// Which devices a hero banner is served to follows from which artwork it
// carries. Mirrors the server (`deriveDeviceType`) and the clients' filters:
// apps + mobile web take mobile|all, desktop web takes desktop|all.
export const deriveDeviceType = (slot, imageUrl, desktopImageUrl) => {
  if (!slotHasDesktopArtwork(slot)) return "all";
  const hasPhone = !!imageUrl;
  const hasDesktop = !!desktopImageUrl;
  if (hasPhone && hasDesktop) return "all";
  if (hasDesktop) return "desktop";
  return "mobile";
};

// A desktop-only hero row keeps its strip in `imageUrl` (required field, and
// what pre-desktopImageUrl web builds render), so the form reads deviceType
// to know which box the URL belongs in. Mirrors `splitArtwork` on the server.
export const splitAdArtwork = (ad) => {
  if (!ad) return { imageUrl: "", desktopImageUrl: "" };
  const hero = slotHasDesktopArtwork(ad.slot);
  const desktopOnly = hero && ad.deviceType === "desktop";
  return {
    imageUrl: desktopOnly ? "" : ad.imageUrl || "",
    desktopImageUrl: hero ? ad.desktopImageUrl || (desktopOnly ? ad.imageUrl || "" : "") : "",
  };
};

// The URL the desktop landing page will actually draw for a row — same
// fallback chain as Lusuku_Web LandingPage.jsx.
export const desktopRenderUrl = (ad) =>
  ad?.desktopImageUrlWebp || ad?.desktopImageUrl || ad?.imageUrl || "";

// One-line, plain-language destination for a saved link.
export const describeLink = (ad) => {
  if (!ad || !ad.linkType || ad.linkType === "none") return "";
  const p = ad.linkParams || {};
  const kindLabel = LINK_KIND_LABEL[p.kind] || "shop";
  if (ad.linkType === "product") {
    if (p.productName) return `${p.productName}${p.vendorName ? ` at ${p.vendorName}` : ""}`;
    return ad.linkTarget || "";
  }
  if (ad.linkType === "vendor") {
    if (p.vendorName) return `${p.vendorName} (${kindLabel})`;
    return ad.linkTarget || "";
  }
  if (ad.linkType === "category") return ad.linkTarget || ad.categoryName || "";
  if (ad.linkType === "external") return ad.linkTarget || ad.externalLink || "";
  return ad.linkTarget || "";
};

// ── Row-level facts for the Ad Center table ───────────────────────────────────
// The table must say what THIS row does, not what its slot could do: which
// surfaces will draw it, with which artwork, and what a tap opens. These two
// describers are the single source of that wording; the table cell and the
// click-to-open details popover both read them.

export const SURFACE_LABELS = {
  android: "Android app",
  ios: "iOS app",
  mweb: "Mobile web",
  desktop: "Desktop web",
};

const HERO_DESKTOP_SIZE = "3159×498";
const sizeLabel = (slot) => {
  const meta = SLOT_META[slot];
  return meta ? `${meta.canonWidthPx}×${meta.canonHeightPx}` : "";
};

/**
 * describePlacement(ad) →
 *   {
 *     slotLabel, reserved,
 *     surfaces: [{ key, label, artwork: "phone" | "desktop" | "phone-cropped" }],
 *     artworks: [{ key, label, size, url }],
 *     warnings: [string],   // things a marketing person must act on
 *     notes: [string],      // facts worth knowing, not problems
 *   }
 * Mirrors the clients' fetch filters exactly: apps and mobile web take
 * deviceType mobile|all, desktop web takes desktop|all; retail_carousel is
 * app-only; reserved slots render nowhere.
 */
export const describePlacement = (ad) => {
  const meta = SLOT_META[ad?.slot];
  const art = splitAdArtwork(ad);
  const dt = ad?.deviceType || "all";
  const surfaces = [];
  const artworks = [];
  const warnings = [];
  const notes = [];

  if (art.imageUrl) artworks.push({ key: "phone", label: "Phone", size: sizeLabel(ad?.slot), url: art.imageUrl });
  if (art.desktopImageUrl) artworks.push({ key: "desktop", label: "Desktop", size: HERO_DESKTOP_SIZE, url: art.desktopImageUrl });

  if (!meta) {
    warnings.push("Unknown slot: no app or web screen renders it.");
    return { slotLabel: ad?.slot || "Unknown slot", reserved: true, surfaces, artworks, warnings, notes };
  }
  if (meta.reserved) {
    warnings.push("Reserved slot: no app or web screen renders it yet, so this banner shows nowhere.");
    return { slotLabel: meta.label, reserved: true, surfaces, artworks, warnings, notes };
  }

  if (ad.slot === "hero_carousel") {
    const phoneServed = dt !== "desktop" && !!art.imageUrl;
    const desktopServed = dt !== "mobile";
    if (phoneServed) {
      surfaces.push({ key: "android", label: SURFACE_LABELS.android, artwork: "phone" });
      surfaces.push({ key: "ios", label: SURFACE_LABELS.ios, artwork: "phone" });
      surfaces.push({ key: "mweb", label: SURFACE_LABELS.mweb, artwork: "phone" });
    } else {
      notes.push("Not in the apps or on mobile web: no phone artwork.");
    }
    if (desktopServed) {
      if (art.desktopImageUrl) {
        surfaces.push({ key: "desktop", label: SURFACE_LABELS.desktop, artwork: "desktop" });
      } else if (art.imageUrl) {
        surfaces.push({ key: "desktop", label: SURFACE_LABELS.desktop, artwork: "phone-cropped" });
        warnings.push("Desktop web crops the phone artwork to a 3159×498 strip (about 28% of its height shows). Upload desktop artwork.");
      }
    } else {
      notes.push("Not on desktop web: no desktop artwork.");
    }
  } else if (ad.slot === "retail_carousel") {
    if (dt === "desktop") {
      warnings.push("Targets desktop web, but this slot only exists in the apps: it shows nowhere.");
    } else {
      surfaces.push({ key: "android", label: SURFACE_LABELS.android, artwork: "phone" });
      surfaces.push({ key: "ios", label: SURFACE_LABELS.ios, artwork: "phone" });
    }
  }

  if (artworks.length === 0) warnings.push("No artwork on this row.");
  return { slotLabel: meta.label, reserved: false, surfaces, artworks, warnings, notes };
};

/**
 * describeLinkState(ad) → { label, detail, tone: "ok" | "muted" | "warn" }
 *   label  : what the LINK pill's second line shows
 *   detail : the full sentence for the details popover
 */
export const describeLinkState = (ad) => {
  const type = ad?.linkType || "none";
  const p = ad?.linkParams || {};
  const target = ad?.linkTarget || "";
  const kindLabel = LINK_KIND_LABEL[p.kind] || "shop";

  if (type === "none") {
    return { label: "Picture only", detail: "Tapping the banner does nothing.", tone: "muted" };
  }
  if (type === "product") {
    if (p.path && p.productName) {
      return {
        label: `${p.productName}${p.vendorName ? ` at ${p.vendorName}` : ""}`,
        detail: `Opens ${p.productName} in the ${p.vendorName || ""} ${kindLabel} in the apps and on the website (${p.path}).`,
        tone: "ok",
      };
    }
    return {
      label: "Not resolved",
      detail: "Saved before product links were validated. Open the banner and pick the product from the list so every surface can open it.",
      tone: "warn",
    };
  }
  if (type === "vendor") {
    if (p.path && p.vendorName) {
      return { label: `${p.vendorName} (${kindLabel})`, detail: `Opens the ${p.vendorName} ${kindLabel} in the apps and on the website (${p.path}).`, tone: "ok" };
    }
    return { label: "Not resolved", detail: "Saved before shop links were validated. Open the banner and pick the shop from the list.", tone: "warn" };
  }
  if (type === "category") {
    const name = target || ad?.categoryName || "";
    return name
      ? { label: `Category: ${name}`, detail: `Opens the shop filtered to the "${name}" category.`, tone: "ok" }
      : { label: "No category set", detail: "Open the banner and type the category name.", tone: "warn" };
  }
  if (type === "external") {
    const value = target || ad?.externalLink || "";
    if (!value) return { label: "No link set", detail: "Open the banner and enter a link or site path.", tone: "warn" };
    const isPath = value.startsWith("/");
    return {
      label: value.replace(/^https?:\/\//, ""),
      detail: isPath ? `Opens the ${value} page of the website, inside the apps and on the web.` : `Opens ${value} in the browser.`,
      tone: "ok",
    };
  }
  return {
    label: `${type === "deal" ? "Deal" : "App screen"} (legacy)`,
    detail: "This link type has no working destination in any app or on the website. Change it to a product, shop, category or link.",
    tone: "warn",
  };
};
